<script lang="ts" setup>
import StepsHeader from '@/components/steps/StepsHeader.vue'
import StepsControls from '@/components/steps/StepsControls.vue'
import StepProjectDetails from '@/components/steps/StepProjectDetails.vue'
import StepHeatPumpSystem from '@/components/steps/heat-pumps/StepHeatPumpSystem.vue'
import StepAccessories from '@/components/steps/accessories/StepAccessories.vue'
import StepSystemSchemes from '@/components/steps/StepSystemSchemes.vue'
import StepSummary from '@/components/steps/summary/StepSummary.vue'
import StepComponents from '@/components/steps/components/StepComponents.vue'
import StepPlanning from '@/components/steps/planning/StepPlanning.vue'
import StepSystemControllers from '@/components/steps/system-controller/StepSystemControllers.vue'
import StepInstallation from '@/components/steps/installation/StepInstallation.vue'
import appStore from '@/store/AppStore'
import { StepsController } from '@/controllers/StepsController'
import projectStore from '@/store/ProjectStore'
import bus from '@/helper/Bus'
import { BusEvent, HpStep, StringMap } from '@/helper/Types'
import FormController from '@/controllers/FormController'
import type { Component } from 'vue'
import { computed, onBeforeMount, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { ModalTypes } from '@/helper/ModalTypes'
import { api } from '@/services/ApiService.ts'
import ProjectController from '@/controllers/ProjectController.ts'
import StepSaleSet from '@/components/steps/sale-set/StepSaleSet.vue'

const currStepComponent = ref()
const COMPONENT_NAME = 'StepsView'
const router = useRouter()

const componentMap: Record<HpStep, Component | null> = {
  [HpStep.PROJECT_DETAILS]: StepProjectDetails,
  [HpStep.PLANNING]: StepPlanning,
  [HpStep.HEAT_PUMP_SYSTEM]: StepHeatPumpSystem,
  [HpStep.SALE_SET]: StepSaleSet,
  [HpStep.COMPONENTS]: StepComponents,
  [HpStep.INSTALLATION]: StepInstallation,
  [HpStep.SYSTEM_CONTROLLERS]: StepSystemControllers,
  [HpStep.ACCESSORIES]: StepAccessories,
  [HpStep.SYSTEM_SCHEMES]: StepSystemSchemes,
  [HpStep.SUMMARY]: StepSummary,
  [HpStep.EMPTY]: null
}

const steps = computed(() => {
  return appStore.steps.map(step => componentMap[step.step])
})

const currentStepTitle = computed((): string => {
  return appStore.getCurrentStepTitle()
})

StepsController.init()

bus.$onC(COMPONENT_NAME, BusEvent.STEP_CHANGED, stepChangedHandler)
bus.$onC(COMPONENT_NAME, BusEvent.FOCUS_FIRST_ELEMENT, () => {
  const el = (document.querySelector('.firstfocus') as HTMLElement) || null
  if (el) {
    el.focus()
  }
})
bus.$onC(COMPONENT_NAME, BusEvent.GO_TO_OVERVIEW, async () => await router.replace({ name: 'ProjectOverviewPage' }))
bus.$onC(COMPONENT_NAME, BusEvent.UPDATE_PROJECT_CONFIRMED, updateProject)

async function updateProject(payload: StringMap): Promise<void> {
  try {
    if (payload.updateProducts.priceUpdate && payload.updateProducts.priceUpdate.length) {
      await api.updateProjectPrices(payload.projectId).then(async () => await ProjectController.loadProject())
    }

  } catch (e) {
    console.error(e)
  }
}

onBeforeMount((): void => {
  window.addEventListener('beforeunload', reloadHandler)
})

onUnmounted((): void => {
  bus.$offC(COMPONENT_NAME)
  window.removeEventListener('beforeunload', reloadHandler)
})

function stepChangedHandler(): void {
  appStore.currentStepComponent = currStepComponent.value
}

function reloadHandler(e: Event): void {
  (document.activeElement as HTMLElement).blur()
  if (FormController.hasUnsavedChanges()) {
    e.preventDefault()
  }
}

async function backToOverviewClicked(): Promise<void> {
  if (FormController.hasUnsavedChanges()) {
    bus.$emit(BusEvent.SHOW_MODAL, {
      modalType: ModalTypes.UNSAVED_CHANGES,
      confirmEventName: BusEvent.GO_TO_OVERVIEW
    })
    return
  }

  bus.$emit(BusEvent.GO_TO_OVERVIEW)
}

function overlayClicked(): void {
  document.body.classList.remove('menu-open')
}
</script>

<template>
  <div>
    <div class="overlay" @click="overlayClicked" />
    <StepsHeader :step-name="currentStepTitle" @btn-overview-clicked="backToOverviewClicked" />
    <div class="main-wrapper">
      <div class="container main">
        <span v-if="projectStore.errorLoadingProject">{{ $t('global.error_loading_project') }}</span>
        <component
          :is="steps[appStore.getStepIndex(appStore.currentStep)]"
          v-if="appStore.currentStep"
          ref="currStepComponent"
        />
        <StepsControls />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@import '@/assets/style/helpers/include-media';

.steps {
  &__content {
    padding-bottom: rem-calc(100);

    @include media('>=desktop') {
      padding-top: rem-calc(72);
    }

    .svg-sprite {
      display: none;
    }
  }

  &__form {
    margin-top: rem-calc(48);

    &.form {
      overflow: visible;
    }

    &:first-of-type {
      margin-top: 0;
    }
  }

  &__form-header {
    display: flex;
  }

  &__info {
    display: block;
    text-align: center;
  }
}

.steps-table {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: rem-calc(16);

    @include media('<tablet') {
      align-items: flex-start;
      flex-direction: column;
    }

    h2 {
      margin-bottom: 0;
    }
  }

  &__title {
    margin-right: auto;

    @include media('>tablet') {
      margin-left: 0 !important;
    }
  }

  &__toggle {
    margin-bottom: 0;
    white-space: nowrap;
    margin-left: rem-calc(8);

    @include media('<tablet') {
      margin-left: 0;
      margin-bottom: 0.75rem;
      flex-direction: row-reverse;
    }

    .form__cm {
      margin-right: 0;
      margin-left: rem-calc(12);

      @include media('<tablet') {
        margin-left: 0;
        margin-right: 0.625rem;
      }
    }
  }
}
</style>
